import React, { useState, useEffect, useCallback, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GoogleMap, useLoadScript, Marker, Autocomplete } from "@react-google-maps/api";
import { toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams, Link } from "react-router-dom";
import Select, { SingleValue } from "react-select";
import { License, Office, Agent, Address, Product } from "types";
import { getAuthCookie } from "../../utils/authCookies";

type ProductObj = {
    product_id: string;
    agent_id: string;
    office_id: string;
    inventory_type: string;
    product: Product;
    created_by: {
        id: string;
        name: string;
    };
};

type Selected = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

const mapContainerStyle = {
    width: "100%",
    height: "400px",
};

const center = {
    lat: 40.712776, // Default latitude (e.g., New York City)
    lng: -74.005974, // Default longitude
};

const libraries: Array<"places"> = ["places"];
const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const SignageTeammemberEditInstallation = () => {
    const { installationId } = useParams<{ installationId: string }>();
    const token = getAuthCookie("sessionToken");
    const userId = getAuthCookie("userId");
    const userName = getAuthCookie("userName");
    const [brokerageFirms, setBrokerageFirms] = useState<License[]>([]);
    const [teamMembers, setTeamMembers] = useState<any[]>([]);
    const [selectedTeamMember, setSelectedTeamMember] = useState<any | null>(null);
    const [installer, setInstaller] = useState<any[]>([]);
    const [offices, setOffices] = useState<Office[]>([]);
    const [officeAgents, setOfficeAgents] = useState<Agent[]>([]);
    const [agents, setAgents] = useState<Agent[]>([]);
    const [preview, setPreview] = useState<string | null>(null);
    const [error, setError] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [quantity, setQuantity] = useState(1);
    const [addressObjects, setAddressObjects] = useState<any[]>([]);
    const [showAllocateModal, setShowAllocateModal] = useState(false);
    const [showViewInventoryModal, setShowViewInventoryModal] = useState(false);

    const [createSignage, setCreateSignage] = useState<License>();
    const [installationOrder, setInstallationOrder] = useState<any>();
    const [productObjs, setProductObjs] = useState<ProductObj[]>([]);
    const [selectedProductOption, setSelectedProductOption] = useState<any | null>(null);
    const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
    const [installationCost, setInstallationCost] = useState<number | null>(null);
    const [scheduledJobsCount, setScheduledJobsCount] = useState<number | null>(null);
    const [totalUnits, setTotalUnits] = useState<number>(0);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [uploadedFileMeta, setUploadedFileMeta] = useState<any[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>([]);
    const [openProductStatusIndex, setOpenProductStatusIndex] = useState<number | null>(null);
    const [rotationalPoolProducts, setRotationalPoolProducts] = useState<any[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
    const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
    const [selectedWarehouse, setSelectedWarehouse] = useState<any | null>(null);
    const [availableSerializedUnits, setAvailableSerializedUnits] = useState<any[]>([]);
    const [allocatedUnits, setAllocatedUnits] = useState<{
        [linkedProductId: string]: {
            rotationalProductId: string;
            warehouseId: string;
            serial_id: string;
            serial_number: string;
            product_name: string;
            sku: string;
        }[];
    }>({});

    const [linkedProductIdContext, setLinkedProductIdContext] = useState<string | null>(null);

    const navigate = useNavigate();

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: apiKey,
        libraries,
    });

    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

    const [selectedLocation, setSelectedLocation] = useState<{
        lat: number;
        lng: number;
    } | null>(null);

    const [selectedBrokerageFirm, setSelectedBrokerageFirm] = useState<Selected | null>(null);

    const [selectedOffice, setSelectedOffice] = useState<Selected | null>(null);

    const [selectedOfficeAgent, setSelectedOfficeAgent] = useState<Selected | null>(null);

    const [selectedInstaller, setSelectedInstaller] = useState<Selected | null>(null);

    const [selectedAgent, setSelectedAgent] = useState<Selected | null>(null);

    const [selectedBillingAddress, setSelectedBillingAddress] =
        useState<Address | null>(null);
    const [selectedOfficeBillingAddress, setSelectedOfficeBillingAddress] =
        useState<Address | null>(null);

    const [installationAddress, setInstallationAddress] = useState<Address>();

    const buyerOptions: { value: string; label: string }[] = [
        { value: "brokerageFirm", label: "Brokerage Firm" },
        { value: "agent", label: "Agent" },
    ];

    const brokerageOptions = brokerageFirms?.map((office) => ({
        value: office.id,
        label: office?.firmDetails?.companyName + "(" + office?.firstName + ")",
        firstName: office?.firstName,
        lastName: office?.lastName,
        email: office?.email,
        phone: office?.phone1,
    }));

    const teamMemberOptions = teamMembers?.map((member) => ({
        value: member.id,
        label: `${member.firstName} ${member.lastName}`,
        email: member.email,
    }));

    const officeOptions = offices?.map((office) => ({
        value: office?._id,
        label: office?.officeName,
        firstName: office?.officeName,
        lastName: office?.officeName,
        email: "",
        phone: office?.officeAddress?.[0]?.phone,
    }));

    const officeAgentOptions = officeAgents?.map((agent) => ({
        value: agent.id,
        label: agent.firstName + " " + agent.lastName,
        firstName: agent.firstName,
        lastName: agent.lastName,
        email: agent.email,
        phone: agent.phone1,
    }));

    const agentOptions = agents?.map((agent) => ({
        value: agent.id,
        label: agent.firstName + " " + agent.lastName,
        firstName: agent.firstName,
        lastName: agent.lastName,
        email: agent.email,
        phone: agent.phone1,
    }));

    const productOptions = productObjs?.map((item) => ({
        value: item.product_id,
        label: item.product.name,
    }));

    const priorityOptions = [
        { value: 1, label: "High" },
        { value: 2, label: "Medium" },
        { value: 3, label: "Low" },
    ];

    useEffect(() => {
        return () => {
            if (preview) URL.revokeObjectURL(preview);
        };
    }, [preview]);

    useEffect(() => {
        const fetchLicense = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/users/${userId}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch license data");
                }

                const data = await response.json();
                console.log("Create Signage Data:", data);
                if (data) {
                    setCreateSignage(data);
                }

            } catch (error) {
                console.error("Error fetching license data:", error);
                setError("Failed to fetch license data.");
            } finally {
                setLoading(false);
            }
        };


        fetchLicense();

    }, [userId, token]);

    useEffect(() => {
        const fetchOrderDetails = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/installation_orders/${installationId}`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                const data = await response.json();
                console.log("Installation Orders:", data);
                if (!response.ok) throw new Error(data.message || "Failed to fetch order details");

                setInstallationOrder(data);
            } catch (err: any) {
                console.error("Order Detail Fetch Error:", err.message);
            }
        };

        fetchOrderDetails();
    }, [installationId, token]);

    useEffect(() => {
        if (installationOrder) {
            const addr = installationOrder?.installation_details?.installation_address;
            setInstallationAddress(addr);
        }
    }, [installationOrder]);

    const fetchBrokerageFirms = async (userType: string) => {
        formik.setFieldValue("userType", userType);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/brokerage-firms`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = await response.json();
            console.log("fetchBrokerageFirms data :", data);

            if (!response.ok) {
                // throw new Error("Failed to fetch agents");
                toast.error(data.error || "Failed to fetch firms");
            }

            setBrokerageFirms(data);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
        } finally {
            setLoading(false);
        }
    };

    const featchBrokerageTeammembers = async (brokerage: any) => {
        setSelectedBrokerageFirm(brokerage);
        console.log("selectedBrokarageFirms:", brokerage);
        let brokerageId = brokerage?.value;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/users-search?created_by.id=${brokerageId}&licenseType=BROKERAGE_TEAM_MEMBER`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            const data = await response.json();
            console.log("featchBrokerageTeammembers :", data);

            if (!response.ok) {
                toast.error(data.error || "Failed to fetch offices");
            } else {
                setTeamMembers(data);
                const normalizedOffices = data.flatMap((member: any) =>
                    (member.offices || []).map((office: any) => ({
                        _id: office._id,
                        officeName: office.officeName,
                        officeType: office.officeType,
                        officeAddress: [office.officeAddress], // ✅ wrap in array to match expected type
                    }))
                );

                setOffices(normalizedOffices);
                // ✅ Set selectedOffice based on installationOrder
                if (installationOrder?.office?._id && normalizedOffices.length > 0) {
                    const matchedOffice = normalizedOffices.find(
                        (office: any) => office._id === installationOrder.office._id
                    );
                    if (matchedOffice) {
                        const officeOption = {
                            value: matchedOffice._id,
                            label: matchedOffice.officeName,
                            firstName: matchedOffice.officeName,
                            lastName: matchedOffice.officeName,
                            email: "",
                            phone: matchedOffice.officeAddress?.[0]?.phone || "",
                        };
                        setSelectedOffice(officeOption);
                        fetchLinkedProducts("office", officeOption.value);
                        fetchOfficeAgents(officeOption);
                    }
                }


                // console.log("offices :", normalizedOffices);
            }
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
        } finally {
            setLoading(false);
        }
    };


    const fetchOfficeAgents = async (office: any) => {
        setSelectedOffice(office);
        console.log("selectedOffices In fetchOfficeAgents:", office);
        let officeId = office?.value;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/agent/office/${officeId}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            const data = await response.json();
            console.log("officeAgents :", data);

            if (!response.ok) {
                toast.error(data.error || "Failed to fetch agents");
            } else {
                setOfficeAgents(data);
            }
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/users/signage/${userId}/agents`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch agents");
                }

                const data: Agent[] = await response.json();
                console.log("signage agents :", data);
                setAgents(data);
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : "An unknown error occurred"
                );
            } finally {
                setLoading(false);
            }
        };

        fetchAgents();
    }, [token]);

    const fetchUserAddresses = async (user: any) => {
        // console.log("fetchUserAddresses IN")
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/user-addresses/${user?.value}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            const data = await response.json();
            console.log("user addresses :", data);
            // setAddresses(data.addresses);
            setAddressObjects(data);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
        } finally {
            setLoading(false);
        }
    };

    const fetchOfficeAddresses = async (office: any) => {
        // console.log("fetchOfficeAddresses IN")
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/offices/${office?.value}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            const data = await response.json();
            console.log("office addresses :", data.officeAddress);
            setAddressObjects(data.officeAddress);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (installationOrder?.buyer_type === "brokerageFirm") {
            console.log("brokerageFirm IN")
            console.log("selectedOffice:", selectedOffice);
            if (selectedOfficeAgent) {
                fetchUserAddresses(selectedOfficeAgent);
            } else if (selectedOffice) {
                fetchOfficeAddresses(selectedOffice);
            }
        } else if (formik.values.userType === "agent") {
            console.log("Agents IN")
            if (selectedAgent) {
                fetchUserAddresses(selectedAgent);
            }
        }
    }, [
        installationOrder?.buyer_type,
        selectedAgent,
        selectedOfficeAgent,
        selectedOffice,
        token,
    ]);

    useEffect(() => {
        if (selectedOfficeAgent) {
            fetchUserAddresses(selectedOfficeAgent);
        } else if (selectedAgent) {
            fetchUserAddresses(selectedAgent);
        } else if (selectedOffice) {
            fetchOfficeAddresses(selectedOffice);
        }
    }, [selectedAgent, selectedOfficeAgent, selectedOffice, token]);

    const addressOptions =
        addressObjects !== null
            ? addressObjects?.map((address) => ({
                ...address,
                value: address.address_details ? address.address_details._id : address._id,
                label: address.label,
            }))
            : [];

    const fetchLinkedProducts = async (
        userType: "agent" | "office",
        user_Id: string,
    ) => {
        console.log("fetchLinkedProducts IN");
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/inventory/${userType}/${user_Id}/signage/${createSignage?.created_by?.id}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = await response.json();
            console.log("Linked Products:", data);
            setProductObjs(data);

            if (!response.ok) {
                toast.error(data?.error || "Failed to fetch linked products");
            } else {
                // setLinkedProducts(data); if needed
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Unknown error occurred");
        }
    };

    // we are fetching signage team members for showing Assigned To dropdown
    useEffect(() => {
        const featchSignageTeammembers = async () => {
            // console.log("featchSignageTeammembers IN", createSignage?.created_by?.id);
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/users-search?created_by.id=${createSignage?.created_by?.id}&licenseType=SIGNAGE_TEAM_MEMBER`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                const data = await response.json();
                console.log("featchSignageTeammembers:", data);

                const installers = data.map((user: any) => ({
                    value: user.id,
                    label: `${user.firstName} ${user.lastName}`,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    email: user.email,
                    phone: user.phone1,
                }));

                setInstaller(installers); // 🟢 Now it's filtered and formatted
            } catch (err) {
                setError(err instanceof Error ? err.message : "An unknown error occurred");
            } finally {
                setLoading(false);
            }
        };

        featchSignageTeammembers();
    }, [token, userId, createSignage]);

    // based on instaletion Address we are calling the dyniamc cost
    useEffect(() => {
        const fetchInstallationCost = async () => {
            if (!installationAddress?.latitude || !installationAddress?.longitude) return;

            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/installation_orders/dynamic_cost/${createSignage?.created_by?.id}`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            installation_address: {
                                addressLine1: installationAddress.addressLine1,
                                addressLine2: installationAddress.addressLine2,
                                city: installationAddress.city,
                                state: installationAddress.state,
                                country: installationAddress.country,
                                zip: installationAddress.zip,
                                cityCode: installationAddress.cityCode,
                                stateCode: installationAddress.stateCode,
                                countryCode: installationAddress.countryCode,
                                latitude: installationAddress.latitude,
                                longitude: installationAddress.longitude,
                            }
                        }),
                    }
                );

                const data = await response.json();
                console.log("Installation cost:", data);
                if (!response.ok) throw new Error(data?.error || "Failed to fetch cost");

                setInstallationCost(data.cost || 0); // adjust based on response shape
            } catch (error: any) {
                console.error("Cost fetch error:", error.message);
                setInstallationCost(null);
            }
        };

        fetchInstallationCost();
    }, [installationAddress, token, createSignage]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            userType: installationOrder?.user_type,
            userFirstName: "",
            userLastName: "",
            userEmail: installationOrder?.user_email,
            userPhone: installationOrder?.user_phone,
            brokerageFirmsId: installationOrder?.brokerage_firm_id,
            teamMemberId: installationOrder?.team_member_id,
            company: "",
            office: installationOrder?.office,
            agent: installationOrder?.agent_id,
            scheduled_date: installationOrder?.installation_details?.scheduled_date?.split("T")[0] || "",
            jobs_today: 0,
            installeId: installationOrder?.installation_details.installer_id,
            priority: installationOrder?.priority,
            billing_address: installationOrder?.billing_address,
            installation_address: installationOrder?.installation_details.installation_address,
            isSameAsBilling: false,
            billingAddressId: installationOrder?.billing_address._id,
            installationAddressId: installationOrder?.installation_details.installation_address.label || "",
            location: "",
            products: "",
            instructions: "",
            payment_method: "",
            image: null,
            installation_charge: installationOrder?.installation_cost,
            discount: installationOrder?.discount_amount,
            subtotal: installationOrder?.subtotal_amount,
            total: installationOrder?.total_amount,
            tax: 0,
            localLawsChecked: installationOrder?.local_laws_checked,
        },
        validationSchema: Yup.object({
            // installer: Yup.string().required("Installer is required"),
            // installation_date: Yup.string().required("Installation date is required"),
            // billing_address: Yup.string().required("Billing address is required"),
            // installation_address: Yup.string().required(
            //   "Installation address is required"
            // ),
            // image: Yup.mixed().required("Image is required"),
        }),
        onSubmit: async (values) => {
            setFormSubmitting(true);
            // Example file upload handling
            const imageUrl = values.image ? await uploadFile(values.image) : null;

            let officeInfo = null;
            let agentId = null;
            let sourceId = null;
            let agentName = null
            let customerId = null;
            let inventorySource = "agent"; // default fallback
            let finalBillingAddress = null;
            let finalInstallationAddress = null;
            if (selectedOfficeAgent) {
                agentId = selectedOfficeAgent?.value;
                sourceId = selectedOfficeAgent?.value
                agentName = selectedOfficeAgent?.label
                customerId = selectedOfficeAgent?.value;
                inventorySource = "agent";
                officeInfo = null;
                finalBillingAddress = selectedBillingAddress;
                finalInstallationAddress = installationAddress;
            } else if (selectedAgent) {
                agentId = selectedAgent?.value;
                sourceId = selectedAgent?.value;
                agentName = selectedAgent?.label
                customerId = selectedAgent?.value;
                inventorySource = "agent";
                officeInfo = null;
                finalBillingAddress = selectedBillingAddress;
                finalInstallationAddress = installationAddress;
            } else if (selectedOffice) {
                const selectedOfficeData = offices?.find(
                    (office) => office._id === selectedOffice?.value
                );
                const primaryAddress = selectedOfficeData?.officeAddress?.[0];
                officeInfo = {
                    _id: selectedOffice?.value,
                    officeName: selectedOffice?.label,
                    officeType: selectedOffice?.label,
                    officeAddress: primaryAddress,
                    status: true,
                };
                sourceId = selectedOffice?.value;
                inventorySource = "office";
                agentId = null;
                agentName = null
                finalBillingAddress = selectedOfficeBillingAddress;
                finalInstallationAddress = installationAddress;
            }

            let lineItems: any[] = [];

            // 📦 1. Regular products (agent or office)
            lineItems = selectedProducts
                .filter((product) => product.inventory_type !== "rotational") // ✅ only agent/office
                .map((product) => {
                    const matched = productObjs.find((p) => p.product_id === product.product_id);
                    const allocated = allocatedUnits[product.product_id] || [];
                    // 🧠 Check if quantity changed from original order
                    const originalItem = installationOrder?.line_items?.find(
                        (item: any) => item.product_id === product.product_id
                    );
                    const originalQty = Number(originalItem?.quantity || 0);
                    const currentQty = Number(product.quantity || 0);
                    const quantityUnchanged = originalQty === currentQty

                    return {
                        product_id: product.product_id,
                        inventory_source: matched?.inventory_type || "", // agent or office
                        warehouse_id: null,
                        serial_ids: [],
                        product_name: matched?.product?.name || "",
                        sku: matched?.product?.sku || "",
                        quantity: Number(product.quantity),
                        unit_price: Number(matched?.product?.retail_price || 0),
                        total_price: Number(matched?.product?.retail_price || 0) * Number(product.quantity),
                        discount: 0,
                        tax_amount: 0,
                        inventory_deducted: false,
                        fulfillment_type: "stock",
                        production_status: product.productionStatus || "not started",
                        is_rotational: matched?.product?.is_rotational || false,
                        current_status: matched?.product?.current_status || "",
                        notes: product.notes || "",
                        rotational_allocated: quantityUnchanged ? allocated.length : 0, // ✅ here’s the fix
                    };
                });

            // 🔄 2. Rotational allocations (only push if linked product quantity hasn't changed)
            Object.entries(allocatedUnits).forEach(([linkedProductId, units]) => {
                if (units.length === 0) return;

                // Get the original line item for this linked product
                const originalItem = installationOrder?.line_items?.find(
                    (item: any) => item.product_id === linkedProductId
                );

                // Get the current (edited) selected product
                const updatedProduct = selectedProducts.find(
                    (p) => p.product_id === linkedProductId
                );

                if (!originalItem || !updatedProduct) return;

                const originalQty = Number(originalItem.quantity || 0);
                const currentQty = Number(updatedProduct.quantity || 0);

                // ❌ Skip if quantities are different
                if (originalQty !== currentQty) return;

                // ✅ Push rotational product allocation
                lineItems.push({
                    source_product_id: linkedProductId,
                    product_id: units[0].rotationalProductId,
                    inventory_source: "rotational",
                    warehouse_id: units[0].warehouseId || "",
                    serial_ids: units.map((u) => u.serial_id),
                    serial_codes: units.map((u) => u.serial_number),
                    product_name: units[0].product_name || "",
                    sku: units[0].sku || "",
                    quantity: 1,
                    unit_price: 0,
                    total_price: 0,
                    discount: 0,
                    tax_amount: 0,
                    inventory_deducted: false,
                    fulfillment_type: "stock",
                    production_status: null,
                    is_rotational: true,
                    current_status: "",
                    notes: "",
                });
            });


            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/installation_orders/update/${installationId}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        // order_number: installationOrder?.order_number,
                        // inventory_source: inventorySource,
                        // source_id: sourceId,
                        // brokerage_firm_id: selectedBrokerageFirm?.value,
                        // brokerage_firm_name: selectedBrokerageFirm?.label,
                        // team_member_id: values.teamMemberId,
                        // office: officeInfo,
                        // agent_id: agentId,
                        // agent_name: agentName,
                        // signage_id: userId,
                        // signage_company_name: userName,
                        line_items: lineItems,
                        // customer_group_id: null,
                        // payment_method: values.payment_method,
                        installation_details: {
                            scheduled_date: new Date(values.scheduled_date).toISOString(),
                            installation_address: finalInstallationAddress,
                            installer_id: selectedInstaller?.value,
                            installer_name: selectedInstaller?.label,
                            contact_number: selectedInstaller?.phone,
                            estimated_duration: null,
                            actual_installation_date: null,
                            installation_notes: values.instructions,
                            assignee_id: selectedInstaller?.value,
                            tax_details: null,
                        },
                        billing_address: finalBillingAddress,
                        additional_instructions: values.instructions,
                        // local_laws_compliance: true,
                        priority: values.priority,
                        uploaded_files: uploadedFileMeta || [],
                        subtotal_amount: values.subtotal,
                        installation_cost: parseFloat(String(installationCost)) || 0,
                        additional_cost: 0,
                        // tax_amount: 0,
                        discount_amount: values.discount,
                        total_amount: values.total,
                        // overpaid_amount: 0,
                        version: installationOrder?.version,
                        // tax_inclusive: false,
                        // tax_details: null,
                    }),
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.message || "Failed to create installation order");
                }

                toast.success("Installation order created successfully!");
                navigate("/signageteammember/jobs/installations");
            } catch (error: any) {
                toast.error(`Error: ${error.message}`);
            } finally {
                setFormSubmitting(false);
            }
        }
    });

    useEffect(() => {
        if (formik.values.installeId && installer.length > 0) {
            const matched = installer.find(i => i.value === formik.values.installeId);
            if (matched) setSelectedInstaller(matched);
        }
    }, [formik.values.installeId, installer]);

    const uploadFile = async (file: File): Promise<string> => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("uploadDir", "installation_orders");

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    setUploadProgress((event.loaded / event.total) * 100);
                }
            };

            xhr.onload = () => {
                if (xhr.status === 200 || xhr.status === 201) {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response.path);
                } else {
                    reject(new Error("Failed to upload file"));
                }
            };

            xhr.onerror = () => reject(new Error("Network error during file upload"));
            xhr.send(formData);
        });
    };

    const handleLocationChange = (
        selectedOption: SingleValue<{ value: string; label: string }>
    ) => {
        formik.setFieldValue("location", selectedOption?.value || "");
    };

    const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            setSelectedLocation({ lat, lng });
            formik.setFieldValue("location", { lat, lng });
        }
    }, []);

    const handlePlaceChanged = () => {
        if (autocompleteRef.current) {
            const place = autocompleteRef.current.getPlace();
            console.log("Place changed:", place);

            const addressComponents = place.address_components;
            console.log(addressComponents);

            if (addressComponents) {
                const getComponent = (type: string) => {
                    const component = addressComponents?.find(c => c.types.includes(type));
                    return component ? { long_name: component.long_name, short_name: component.short_name } : { long_name: '', short_name: '' };
                };

                const streetNumber = getComponent('street_number').long_name;
                const route = getComponent('route').long_name;

                const cityObj = getComponent('locality') || getComponent('administrative_area_level_2');
                const stateObj = getComponent('administrative_area_level_1');
                const postalCode = getComponent('postal_code').long_name;
                const countryObj = getComponent('country');


                const street = `${streetNumber} ${route}`.trim();

                const latitude = place.geometry?.location?.lat();
                const longitude = place.geometry?.location?.lng();

                console.log("Latitude:", latitude, "Longitude:", longitude);

                // Update form values
                // formik.setFieldValue('streetAddress1', street);
                // formik.setFieldValue('city', cityObj.long_name);
                // formik.setFieldValue('county', cityObj.long_name);
                // formik.setFieldValue('cityCode', cityObj.short_name); // <-- Add this
                // formik.setFieldValue('state', stateObj.long_name);
                // formik.setFieldValue('stateCode', stateObj.short_name); // <-- Add this
                // formik.setFieldValue('zipcode', postalCode);
                // formik.setFieldValue('country', countryObj.long_name);
                // formik.setFieldValue('countryCode', countryObj.short_name); 

                // if (latitude && longitude) {
                //   formik.setFieldValue('latitude', latitude);
                //   formik.setFieldValue('longitude', longitude);
                // }

                const installation_address = street + ',' + cityObj.long_name + ', ' + stateObj.long_name + ', ' + postalCode;
                setInstallationAddress({
                    _id: '',
                    label: installation_address,
                    addressLine1: street,
                    addressLine2: '',
                    country: countryObj.long_name,
                    county: cityObj.long_name,
                    city: cityObj.long_name,
                    state: stateObj.long_name,
                    zip: postalCode,
                    cityCode: cityObj.short_name,
                    stateCode: stateObj.short_name,
                    countryCode: countryObj.short_name,
                    latitude: latitude || 0,
                    longitude: longitude || 0,
                    phone: '',
                });

                formik.setFieldValue('installationAddressId', installation_address);
            }
        }
    };

    // to get the scheduled job count based on the selected date
    useEffect(() => {
        const fetchScheduledJobs = async () => {
            if (!formik.values.scheduled_date || !userId) return;

            const date = new Date(formik.values.scheduled_date).toISOString().split("T")[0]; // format as YYYY-MM-DD

            try {
                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/installation_orders/${formik.values.installeId}/${date}/scheduled_jobs`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                const data = await res.json();
                if (!res.ok) throw new Error(data.message || "Failed to fetch job count");
                console.log("Scheduled Jobs:", data);

                setScheduledJobsCount(data.scheduled_jobs); // assuming response shape: { count: number }
                formik.setFieldValue("jobs_today", data.scheduled_jobs);
            } catch (err: any) {
                console.error("Scheduled Jobs Error:", err.message);
                setScheduledJobsCount(null);
            }
        };

        fetchScheduledJobs();
    }, [formik.values.installeId, userId, token]);

    // seting the total units based on the selected products
    useEffect(() => {
        const newTotal = selectedProducts
            .filter((p) => p.inventory_type !== "rotational")
            .reduce((sum, product) => sum + (product.quantity || 0), 0);

        setTotalUnits(newTotal);
    }, [selectedProducts]);

    // seting the installation address based on the selected billing address
    useEffect(() => {
        if (formik.values.isSameAsBilling) {
            const isOffice = !selectedOfficeAgent && !selectedAgent && selectedOffice;
            const billing = isOffice ? selectedOfficeBillingAddress : selectedBillingAddress;

            if (billing) {
                console.log("Billing Address:", billing);
                setInstallationAddress(billing);
                formik.setFieldValue("installationAddressId", billing.label || "");
            }
        }
    }, [formik.values.isSameAsBilling, selectedBillingAddress, selectedOfficeBillingAddress]);

    // calculating the subtotal and total
    useEffect(() => {
        const subtotal = Number(installationCost) + Number(formik.values.tax);
        const total = subtotal - Number(formik.values.discount || 0);

        formik.setFieldValue("subtotal", subtotal);
        formik.setFieldValue("total", total);
    }, [installationCost, formik.values.tax, formik.values.discount]);

    // remove the preview images
    useEffect(() => {
        return () => {
            imagePreviews.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [imagePreviews]);

    useEffect(() => {
        if (
            installationAddress?.latitude &&
            installationAddress?.longitude &&
            installationAddress.latitude !== 0 &&
            installationAddress.longitude !== 0
        ) {
            setSelectedLocation({
                lat: installationAddress.latitude,
                lng: installationAddress.longitude,
            });
        }
    }, [installationAddress]);


    const handleMarkerDragEnd = async (event: google.maps.MapMouseEvent) => {
        const lat = event.latLng?.lat();
        const lng = event.latLng?.lng();

        if (!lat || !lng) return;

        setSelectedLocation({ lat, lng });

        try {
            const geocoder = new window.google.maps.Geocoder();
            const response = await geocoder.geocode({ location: { lat, lng } });

            if (response.results[0]) {
                const place = response.results[0];
                const addressComponents = place.address_components;

                const getComponent = (type: string) => {
                    const component = addressComponents.find((c: any) =>
                        c.types.includes(type)
                    );
                    return component
                        ? { long_name: component.long_name, short_name: component.short_name }
                        : { long_name: "", short_name: "" };
                };

                const streetNumber = getComponent("street_number").long_name;
                const route = getComponent("route").long_name;
                const cityObj =
                    getComponent("locality") ||
                    getComponent("administrative_area_level_2");
                const stateObj = getComponent("administrative_area_level_1");
                const postalCode = getComponent("postal_code").long_name;
                const countryObj = getComponent("country");

                const street = `${streetNumber} ${route}`.trim();

                const newAddress = {
                    _id: "",
                    label: place.formatted_address,
                    addressLine1: street,
                    addressLine2: "",
                    country: countryObj.long_name,
                    county: cityObj.long_name,
                    city: cityObj.long_name,
                    state: stateObj.long_name,
                    zip: postalCode,
                    cityCode: cityObj.short_name,
                    stateCode: stateObj.short_name,
                    countryCode: countryObj.short_name,
                    latitude: lat,
                    longitude: lng,
                    phone: "",
                };

                setInstallationAddress(newAddress);
                formik.setFieldValue("installationAddressId", place.formatted_address);
            }
        } catch (error) {
            console.error("Geocoder failed", error);
            toast.error("Unable to update address from marker position.");
        }
    };

    // geting the defult selectedAgent and installetion Addreess and getting the brokarageForms
    useEffect(() => {
        if (installationOrder?.agent_id && agents.length > 0) {
            const matchedAgent = agents.find(agent => agent.id === installationOrder.agent_id);
            if (matchedAgent) {
                const option = {
                    value: matchedAgent.id,
                    label: `${matchedAgent.firstName} ${matchedAgent.lastName}`,
                    firstName: matchedAgent.firstName,
                    lastName: matchedAgent.lastName,
                    email: matchedAgent.email,
                    phone: matchedAgent.phone1,
                };
                setSelectedAgent(option);
                fetchLinkedProducts("agent", option.value);
            }
        }

        if (installationOrder?.installation_details?.installation_address) {
            setInstallationAddress(installationOrder.installation_details.installation_address);
        }

        if (installationOrder?.user_type) {
            fetchBrokerageFirms(installationOrder.user_type);
        }
    }, [installationOrder, agents]);

    // calling brokarage Teammembers based on selected Brokarage
    useEffect(() => {
        if (
            installationOrder?.brokerage_firm_id &&
            brokerageOptions.length > 0
        ) {
            const selectedFirm = brokerageOptions.find(
                (firm) => firm.value === installationOrder.brokerage_firm_id
            );

            if (
                selectedFirm &&
                selectedBrokerageFirm?.value !== selectedFirm.value
            ) {
                console.log("Selected Brokerage Firm:", selectedFirm);
                // setSelectedBrokerageFirm(selectedFirm);
                featchBrokerageTeammembers(selectedFirm);
            }
        }
    }, [installationOrder?.brokerage_firm_id, brokerageOptions.length]);

    // setting defult selectedProducts
    useEffect(() => {
        if (installationOrder?.line_items?.length > 0) {
            const mapped = installationOrder.line_items.map((item: any) => ({
                product_id: item.product_id,
                inventory_type: item.inventory_source || "",
                name: item.product_name || "",
                sku: item.sku || "",
                product_location_status: item.current_status || "",
                user_id: "", // You can enrich this if needed based on agent/office ID from elsewhere
                quantity: item.quantity,
                updateStatus: "",
                productionStatus: item.production_status || "",
                is_rotational: item.is_rotational,
                allocatedCount: item.rotational_allocated || 0,
                source_product_id: item.source_product_id || null,
                serial_ids: item.serial_ids || [],
            }));

            setSelectedProducts(mapped);
            console.log("Mapped Products:", mapped);
        }
    }, [installationOrder]);

    // setting defult allocatedUnits
    useEffect(() => {
        if (!installationOrder?.line_items?.length) return;

        const prefilledAllocations: {
            [linkedProductId: string]: {
                rotationalProductId: string;
                warehouseId: string;
                serial_id: string;
                serial_number: string;
                product_name: string;
                sku: string;
            }[];
        } = {};

        for (const item of installationOrder.line_items) {
            if (
                Array.isArray(item.serial_ids) &&
                item.serial_ids.length > 0 &&
                item.warehouse_id &&
                item.product_id
            ) {
                const key = item.is_rotational ? item.source_product_id : item.product_id;
                if (!key) continue;

                const allocations = item.serial_ids.map((sid: string, index: number) => ({
                    rotationalProductId: item.product_id,
                    warehouseId: item.warehouse_id,
                    serial_id: sid,
                    serial_number: item.serial_codes?.[index] || sid,
                    product_name: item.product_name || "Unknown Product",
                    sku: item.sku || "",
                }));

                prefilledAllocations[key] = allocations;
            }
        }

        setAllocatedUnits(prefilledAllocations);
    }, [installationOrder]);


    useEffect(() => {
        if (!installationOrder || addressOptions.length === 0 || addressObjects.length === 0) return;

        const billingId = formik.values.billingAddressId;
        if (!billingId) return;

        const selectedBilling = addressOptions.find(address => address.value === billingId);
        const isOffice = !selectedOfficeAgent && !selectedAgent && selectedOffice;

        if (selectedBilling) {
            const billingMatch = addressObjects.find(
                (addr) =>
                    (addr.address_details ? addr.address_details._id : addr._id) === selectedBilling.value
            );

            if (isOffice) {
                if (!selectedOfficeBillingAddress || selectedOfficeBillingAddress._id !== billingMatch?._id) {
                    console.log("Auto-setting Office Billing Address:", billingMatch);
                    setSelectedOfficeBillingAddress(billingMatch || null);
                }
            } else {
                const agentBillingDetails = billingMatch?.address_details || null;
                if (!selectedBillingAddress || selectedBillingAddress._id !== agentBillingDetails?._id) {
                    console.log("Auto-setting Agent Billing Address:", agentBillingDetails);
                    setSelectedBillingAddress(agentBillingDetails);
                }
            }
        }
    }, [
        installationOrder,
        formik.values.billingAddressId,
        addressOptions,
        selectedOfficeAgent,
        selectedAgent,
        selectedOffice,
    ]);

    const updateProductionStatus = async (productId: string, productionStatus: string) => {
        if (!installationId || !token) return;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/installation_orders/${installationId}/update_production_status`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        product_id: productId,
                        production_status: productionStatus,
                    }),
                }
            );

            const data = await response.json();
            console.log("Update Production Status Response:", data);

            if (!response.ok) {
                toast.error(data?.error || "Failed to update production status");
                return;
            }

            toast.success("Production status updated successfully!");
        } catch (err: any) {
            toast.error(err.message || "Error updating production status");
        }
    };

    const isFieldEditable = (status: number, field: string): boolean => {
        const editableMap: Record<number, string[]> = {
            100: [
                // Created (Draft)
                "BasicDetails",
                "BillingAddress",
                "InstallationAddress",
                "LineItems",
                "Priority",
                "Instructions",
                "Installer",
                "InstallationDate"
            ],
            200: [
                // Finalized
                "LineItems",
                "InstallationAddress",
                "Instructions",
            ],
            300: [
                // Scheduled or Re-scheduled
                "Priority",
                "InstallationDate", // if ≥ 48h
                "Installer",
                "Instructions",
            ],
            400: [
                // In Progress
                "Instructions",
            ],
            // 500 and 600: No edits allowed
        };

        const nonEditableStatuses = [500, 600]; // Completed, Cancelled

        if (nonEditableStatuses.includes(status)) return false;

        const editableFields = editableMap[status] || [];
        return editableFields.includes(field);
    };

    // const fetchRotationalPoolProducts = async () => {
    //   try {
    //     const response = await fetch(
    //       `${process.env.REACT_APP_API_URL}/signage/${userId}/rotational-products`,
    //       {
    //         method: "GET",
    //         headers: {
    //           Authorization: `Bearer ${token}`,
    //           "Content-Type": "application/json",
    //         },
    //       }
    //     );

    //     const data = await response.json();
    //     if (!response.ok) throw new Error(data.message || "Failed to fetch");
    //     console.log("Rotational Pool Products:", data);

    //     setRotationalPoolProducts(data); // ✅ store in state
    //   } catch (err: any) {
    //     console.error("Error fetching rotational products:", err.message);
    //     toast.error("Failed to fetch rotational pool products");
    //   }
    // };

    // const rotationalproductOptions = rotationalPoolProducts.map((p) => ({
    //   value: p._id,
    //   label: p.rotational_product?.name || "Unnamed Product",
    //   fullProduct: p,
    // }));


    // const handleProductSelect = (selected: any) => {
    //   setSelectedProduct(selected);

    //   const warehouses = selected?.fullProduct?.warehouse_details || [];

    //   const options = warehouses.map((w: any) => ({
    //     value: w.warehouse._id,
    //     label: `${w.warehouse.label} (${w.available} available)`,
    //   }));

    //   setWarehouseOptions(options);
    // };

    // const fetchSerializedUnits = async (rotationalId: string, warehouseId: string) => {
    //   if (!userId || !token) return;

    //   try {
    //     const response = await fetch(
    //       `${process.env.REACT_APP_API_URL}/signage/${userId}/warehouses/rotational-pool/${rotationalId}/warehouse/${warehouseId}/available-serialized-units`,
    //       {
    //         method: "GET",
    //         headers: {
    //           Authorization: `Bearer ${token}`,
    //           "Content-Type": "application/json",
    //         },
    //       }
    //     );

    //     const data = await response.json();
    //     if (!response.ok) throw new Error(data.message || "Failed to fetch serialized pool");
    //     console.log("Serialized Units:", data);
    //     setAvailableSerializedUnits(data);
    //   } catch (err: any) {
    //     console.error("Fetch Serialized Units Error:", err.message);
    //     toast.error("Unable to fetch serialized units");
    //   }
    // };

    // useEffect(() => {
    //   console.log("AllocatedUnits :", allocatedUnits);
    // }, [allocatedUnits]);


    useEffect(() => {
        if (installationOrder?.uploaded_files) {
            setUploadedFileMeta(installationOrder.uploaded_files);
        }
    }, [installationOrder]);

    if (!isLoaded) return <div>Loading...</div>;

    return (
        <div className="content">
            <div className="card">
                <div className="card-body">
                    <h2>Edit Installation Order</h2>
                    <form onSubmit={formik.handleSubmit}>
                        <div className="row mt-5">
                            <div className="col-md-4">
                                <label htmlFor="">Order ID</label>
                                <input type="text" name="orderId" id="orderId" className="form-control" value={installationOrder?.order_number} disabled />
                            </div>

                            <div className="col-md-4 mb-3">
                                <label htmlFor="userType" className="form-label">
                                    Buyer Type
                                </label>
                                <Select
                                    className="form-control"
                                    id="userType"
                                    name="userType"
                                    options={buyerOptions}
                                    value={buyerOptions?.find(
                                        (option) =>
                                            option.value === formik.values.userType
                                    )}
                                    onChange={(selectedOption) => {
                                        formik.setFieldValue("userType", selectedOption?.value)
                                        fetchBrokerageFirms(selectedOption?.value || "")
                                    }
                                    }
                                    isDisabled
                                    onBlur={formik.handleBlur}
                                />
                            </div>
                            {formik.values.userType === "brokerageFirm" ? (
                                <>
                                    <div className="col-md-4 mb-3">
                                        <label htmlFor="brokerageFirmsId">
                                            Select Brokerage Firm
                                        </label>
                                        <Select
                                            className="form-control"
                                            id="brokerageFirmsId"
                                            name="brokerageFirmsId"
                                            options={brokerageOptions}
                                            value={brokerageOptions?.find((option) => option.value === formik.values.brokerageFirmsId) || null}
                                            onChange={(selected) => {
                                                // setBuyerType(selected?.value || "");
                                                featchBrokerageTeammembers(selected || null)
                                                formik.setFieldValue("brokerageFirmsId", selected?.value || "");
                                            }
                                            }
                                            isDisabled
                                            onBlur={formik.handleBlur}
                                        />
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label htmlFor="officeSelection">
                                            Select Team Member
                                        </label>
                                        <Select
                                            id="teamMemberId"
                                            name="teamMemberId"
                                            className="form-control"
                                            options={teamMemberOptions}
                                            value={teamMemberOptions?.find((option) => option.value === formik.values.teamMemberId) || null}
                                            onChange={(selected) => {
                                                formik.setFieldValue("teamMemberId", selected?.value || "");
                                                setSelectedTeamMember(selected);
                                            }}
                                            onBlur={formik.handleBlur}
                                            isDisabled
                                            placeholder="Select a team member"
                                        />
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label htmlFor="officeSelection">
                                            Select Office
                                        </label>
                                        <Select
                                            className="form-control"
                                            id="officeSelection"
                                            options={officeOptions}
                                            value={
                                                officeOptions?.find(
                                                    (option) =>
                                                        option.value === formik.values.office._id
                                                ) || null
                                            }
                                            onChange={(selected) => {
                                                fetchOfficeAgents(selected);
                                                console.log("Selected Office:", selected);
                                                formik.setFieldValue(
                                                    "userFirstName",
                                                    selected?.firstName || ""
                                                );
                                                formik.setFieldValue(
                                                    "userLastName",
                                                    selected?.lastName || ""
                                                );
                                                formik.setFieldValue(
                                                    "userEmail",
                                                    selected?.email || ""
                                                );
                                                formik.setFieldValue(
                                                    "userPhone",
                                                    selected?.phone || ""
                                                );
                                            }}
                                            isDisabled
                                        />
                                    </div>
                                    {officeAgentOptions && (
                                        <div className="col-md-4 mb-3">
                                            <label htmlFor="officeSelection">
                                                Select Agent
                                            </label>
                                            <Select
                                                className="form-control"
                                                id="officeAgentSelection"
                                                options={officeAgentOptions}
                                                value={officeAgentOptions?.find((option) => option.value === formik.values.agent) || null}
                                                onChange={(selected) => {
                                                    setSelectedOfficeAgent(selected);
                                                    // setSelectedAgent(selected);
                                                    formik.setFieldValue("agent", selected?.value || "")
                                                    formik.setFieldValue(
                                                        "userFirstName",
                                                        selected?.firstName || ""
                                                    );
                                                    formik.setFieldValue(
                                                        "userLastName",
                                                        selected?.lastName || ""
                                                    );
                                                    formik.setFieldValue(
                                                        "userEmail",
                                                        selected?.email || ""
                                                    );
                                                    formik.setFieldValue(
                                                        "userPhone",
                                                        selected?.phone || ""
                                                    );
                                                }}
                                                isDisabled
                                            />
                                        </div>
                                    )}
                                </>
                            ) : (
                                formik.values.userType === "agent" && (
                                    <div className="col-md-4 mb-3">
                                        <label htmlFor="agentSelection">
                                            Select Agent
                                        </label>
                                        <Select
                                            className="form-control"
                                            id="agentSelection"
                                            options={agentOptions}
                                            value={agentOptions?.find((option) => option.value === formik.values.agent) || null}
                                            onChange={(selected) => {
                                                setSelectedAgent(selected);
                                                formik.setFieldValue("agent", selected?.value || "")
                                                formik.setFieldValue(
                                                    "userFirstName",
                                                    selected?.firstName || ""
                                                );
                                                formik.setFieldValue(
                                                    "userLastName",
                                                    selected?.lastName || ""
                                                );
                                                formik.setFieldValue(
                                                    "userEmail",
                                                    selected?.email || ""
                                                );
                                                formik.setFieldValue(
                                                    "userPhone",
                                                    selected?.phone || ""
                                                );
                                            }}
                                            onBlur={formik.handleBlur}
                                            isDisabled
                                        />
                                    </div>
                                )
                            )}

                            <div className="col-md-4">
                                <label htmlFor="userEmail" className="form-label">Email</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    id="userEmail"
                                    name="userEmail"
                                    placeholder="Enter email"
                                    value={formik.values.userEmail}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    readOnly={!isFieldEditable(installationOrder?.status ?? 0, "BasicDetails")}
                                />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="userPhone" className="form-label">Phone</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="userPhone"
                                    name="userPhone"
                                    placeholder="Enter phone"
                                    value={formik.values.userPhone}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    readOnly={!isFieldEditable(installationOrder?.status ?? 0, "BasicDetails")}
                                />
                            </div>
                        </div>

                        <h5 className="mt-2">Address Details </h5>
                        <div className="my-2">
                            <input
                                type="checkbox"
                                id="isSameAsBilling"
                                name="isSameAsBilling"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                checked={formik.values.isSameAsBilling}
                            />{" "}
                            Billing and Installation Address are Different

                        </div>
                        <div className="row mb-4">
                            <div className="col-md-5">
                                <div className="mb-3">
                                    <label
                                        htmlFor="billingAddressId"
                                        className="form-label"
                                    >
                                        Billing Address
                                    </label>
                                    <Select
                                        className="form-control"
                                        id="billingAddress"
                                        name="billingAddress"
                                        options={addressOptions}
                                        value={
                                            addressOptions?.find(
                                                (option) =>
                                                    option.value === formik.values.billingAddressId
                                            ) || null
                                        }
                                        onChange={(selected: any) => {
                                            console.log("Selected Billing Address:", selected);
                                            formik.setFieldValue("billingAddressId", selected?.value); // ✅ Corrected here
                                            const isOffice = !selectedOfficeAgent && !selectedAgent && selectedOffice;
                                            console.log("isOffice :", isOffice);

                                            const selectedAddress = addressObjects?.find(
                                                (addr) => (addr.address_details ? addr.address_details._id : addr._id) === selected?.value
                                            );

                                            if (isOffice) {
                                                setSelectedOfficeBillingAddress(selectedAddress || null);
                                            } else {
                                                setSelectedBillingAddress(selectedAddress?.address_details || null);
                                            }
                                        }}
                                        isDisabled={!isFieldEditable(installationOrder?.status ?? 0, "BillingAddress")}

                                    />
                                </div>
                            </div>

                            {formik.values.isSameAsBilling === true ? null : (
                                <div className="col-md-5">
                                    <div className="mb-3">
                                        <label
                                            htmlFor="installationAddressId"
                                            className="form-label"
                                        >
                                            Installation Address
                                        </label>
                                        {isLoaded && (
                                            <Autocomplete
                                                onLoad={(autocomplete) => {
                                                    autocompleteRef.current = autocomplete;
                                                }}
                                                onPlaceChanged={handlePlaceChanged}
                                            >
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="installationAddressId"
                                                    name="installationAddressId"
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    value={formik.values.installationAddressId}
                                                    readOnly={!isFieldEditable(installationOrder?.status ?? 0, "InstallationAddress")}
                                                />
                                            </Autocomplete>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="col-md-2">
                                <p>&nbsp;</p>
                                <i className="ri-search-line fs-3 text-bg-info"></i>
                            </div>

                        </div>

                        <div className="row mb-3">
                            <h5>Map View</h5>
                            <div style={mapContainerStyle}>
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    zoom={10}
                                    center={selectedLocation || center}
                                    // onClick={handleMapClick}
                                >
                                    {selectedLocation && <Marker
                                        position={selectedLocation}
                                        draggable={true}
                                        onDragEnd={handleMarkerDragEnd}
                                    />}
                                </GoogleMap>
                            </div>
                            {formik.touched.location && formik.errors.location && (
                                <div className="text-danger">{formik.errors.location}</div>
                            )}
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-4 mb-3">
                                <label className="form-label">
                                    Scheduled Date <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    id="scheduled_date"
                                    name="scheduled_date"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.scheduled_date}
                                    readOnly={!isFieldEditable(installationOrder?.status ?? 0, "InstallationDate")}
                                />
                                {/* {formik.touched.scheduled_date &&
                  formik.errors.scheduled_date && (
                    <div className="invalid-feedback">
                      {formik.errors.scheduled_date}
                    </div>
                  )} */}
                            </div>
                            <div className="col-md-2 mb-3">
                                <label className="form-label">
                                    Jobs Today
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="jobs_today"
                                    name="jobs_today"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.jobs_today}
                                    disabled
                                />
                                {formik.touched.jobs_today &&
                                    formik.errors.jobs_today && (
                                        <div className="invalid-feedback">
                                            {formik.errors.jobs_today}
                                        </div>
                                    )}
                            </div>
                            <div className="col-md-4 mb-3">
                                <label className="form-label">
                                    Assigned To
                                </label>
                                <Select
                                    id="installeId"
                                    name="installeId"
                                    options={installer}
                                    value={installer?.find((option) => option.value === formik.values.installeId) || null}
                                    onChange={(selected) => {
                                        setSelectedInstaller(selected)
                                        formik.setFieldValue("installeId", selected?.value || "")

                                    }
                                    }
                                    onBlur={formik.handleBlur}
                                    isDisabled={!isFieldEditable(installationOrder?.status ?? 0, "Installer")}
                                />
                                {/* {formik.touched.installeId && formik.errors.installeId && (
                  <div className="text-danger">{formik.errors.installeId}</div>
                )} */}
                            </div>
                            <div className="col-md-2 mb-3">
                                <label className="form-label">
                                    Priority
                                </label>
                                <Select
                                    id="priority"
                                    name="priority"
                                    options={priorityOptions}
                                    value={priorityOptions.find((option) => option.value === formik.values.priority) || null}
                                    onChange={(option) =>
                                        formik.setFieldValue("priority", option?.value || "")
                                    }
                                    onBlur={formik.handleBlur}
                                    isDisabled={!isFieldEditable(installationOrder?.status ?? 0, "Priority")}
                                />
                                {/* {formik.touched.priority && formik.errors.priority && (
                  <div className="text-danger">{formik.errors.priority}</div>
                )} */}
                            </div>
                        </div>

                        <div className="col-md-12">
                            <h4>Products</h4>
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <Select
                                        id="products"
                                        name="products"
                                        options={productOptions}
                                        value={selectedProductOption}
                                        onChange={(selected) => {
                                            setSelectedProductOption(selected)
                                        }}
                                        onBlur={formik.handleBlur}
                                        isDisabled={!isFieldEditable(installationOrder?.status ?? 0, "LineItems")}
                                    />

                                    {formik.touched.products && formik.errors.products && (
                                        <div className="text-danger">{formik.errors.products}</div>
                                    )}
                                </div>
                                <div className="col-md-3">
                                    <button
                                        className="btn btn-success"
                                        type="button"
                                        onClick={() => {
                                            if (!selectedProductOption) return;

                                            const alreadyExists = selectedProducts.some(
                                                (prod) => prod.product_id === selectedProductOption.value
                                            );
                                            if (alreadyExists) {
                                                toast.warning("Product already added");
                                                return;
                                            }

                                            const fullProduct = productObjs.find(
                                                (p) => p.product_id === selectedProductOption.value
                                            );

                                            if (fullProduct) {
                                                const flattenedProduct = {
                                                    product_id: fullProduct.product_id,
                                                    name: fullProduct.product.name,
                                                    sku: fullProduct.product.sku || "",
                                                    inventory_type: fullProduct.inventory_type,
                                                    product_location_status: fullProduct.product.product_location_status,
                                                    user_id: fullProduct.agent_id || fullProduct.office_id,
                                                    quantity: 1,
                                                    updateStatus: "",
                                                    productionStatus: "",
                                                };

                                                setSelectedProducts((prev) => [...prev, flattenedProduct]);
                                                setSelectedProductOption(null);
                                            }
                                            console.log("selectedProducts", selectedProducts);
                                        }}
                                        disabled={!isFieldEditable(installationOrder?.status ?? 0, "LineItems")}
                                    >
                                        Add Product
                                    </button>

                                </div>
                            </div>
                            <div className="row mb-5">
                                <div className="col-md-12">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th className="text-center">Product Name</th>
                                                <th className="text-center">SKU</th>
                                                <th className="text-center">Qty </th>
                                                <th className="text-center">Source </th>
                                                <th className="text-center">Product Location Status</th>
                                                <th className="text-center">Production Status </th>
                                                <th className="text-center">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                selectedProducts?.filter((p) => p.inventory_type !== "rotational" && p.inventory_type !== "").map((item, index) => (
                                                    <tr key={item.product_id}>
                                                        <td className="text-center">{item.name}</td>
                                                        <td className="text-center">{item.sku}</td>
                                                        <td className="text-center">
                                                            <input
                                                                type="number"
                                                                className="form-control text-center"
                                                                value={item.quantity}
                                                                min={1}
                                                                onChange={(e) => {
                                                                    const qty = Number(e.target.value);
                                                                    const updated = [...selectedProducts];
                                                                    updated[index].quantity = qty;
                                                                    setSelectedProducts(updated);
                                                                }}
                                                            />

                                                        </td>
                                                        <td className="text-center">
                                                            {item.inventory_type === "agent" ? "Agent Inventory" : item.inventory_type === "rotational" ? "Rotational Inventory" : "Office Inventory"}
                                                        </td>

                                                        <td className="text-center">
                                                            <div className="d-flex justify-content-evenly">
                                                                <span className="bg-warning px-3 py-2" style={{ borderRadius: "10px" }}>
                                                                    {item.product_location_status?.replace(/_/g, " ") || "N/A"}
                                                                </span>
                                                                <Link
                                                                    className="btn btn-small btn-primary"
                                                                    to={
                                                                        item.inventory_type === "agent"
                                                                            ? "/signageteammember/incomingAgentProducts"
                                                                            : "/signageteammember/incomingOfficeProducts"
                                                                    }
                                                                >
                                                                    Update Status
                                                                </Link>
                                                            </div>
                                                            {/* {openStatusIndex === index && (
                                <>
                                  <div className="mt-2">
                                    <Select
                                      id="update_status"
                                      name="update_status"
                                      options={[
                                        { value: "with supplier", label: "With Supplier" },
                                        { value: "in transit", label: "In Transit" },
                                        { value: "on site", label: "On Site" },
                                        { value: "declined", label: "Declined" },
                                      ]}
                                      onChange={(option) =>
                                        formik.setFieldValue("company", option?.value || "")
                                      }
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>
                                  <div className="mt-2">
                                    <button className="btn btn-info small w-100">Update</button>
                                  </div>
                                </>
                              )} */}
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="d-flex justify-content-evenly">
                                                                <span className="bg-secondary px-3 py-2 text-white" style={{ borderRadius: "10px" }}>
                                                                    {item.productionStatus?.replace(/_/g, " ") || "Not Started"}
                                                                </span>
                                                                <button type="button"
                                                                    className="btn btn-sm btn-primary"
                                                                    onClick={() =>
                                                                        setOpenProductStatusIndex(openProductStatusIndex === index ? null : index)
                                                                    }
                                                                >
                                                                    Update Status
                                                                </button>
                                                            </div>

                                                            {openProductStatusIndex === index && (
                                                                <>
                                                                    <div className="mt-2">
                                                                        <Select
                                                                            id={`product_status_${index}`}
                                                                            name={`product_status_${index}`}
                                                                            options={[
                                                                                { value: "not_started", label: "Not Started" },
                                                                                { value: "in_queue", label: "In Queue" },
                                                                                { value: "in_production", label: "In Production" },
                                                                                { value: "quality_check", label: "Quality Check" },
                                                                                { value: "ready_for_pickup", label: "Ready For Pickup" },
                                                                                { value: "ready_for_installation", label: "Ready for Installation" },
                                                                            ]}
                                                                            value={{ value: item.productionStatus, label: item.productionStatus }}
                                                                            onChange={(option) => {
                                                                                const updated = [...selectedProducts];
                                                                                updated[index].productionStatus = option?.value || "";
                                                                                setSelectedProducts(updated);
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <div className="mt-2">
                                                                        <button
                                                                            className="btn btn-info btn-sm w-100"
                                                                            onClick={() => {
                                                                                const selectedStatus = selectedProducts[index].productionStatus;
                                                                                const productId = selectedProducts[index].product_id;
                                                                                updateProductionStatus(productId, selectedStatus);
                                                                                setOpenProductStatusIndex(null);
                                                                            }}
                                                                        >
                                                                            Update
                                                                        </button>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </td>

                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-danger btn-sm"
                                                                onClick={() => {
                                                                    const removedProductId = item.product_id;

                                                                    // 1. Remove the product from selectedProducts
                                                                    const updatedProducts = selectedProducts.filter((p) => p.product_id !== removedProductId);
                                                                    setSelectedProducts(updatedProducts);

                                                                    // 2. Remove only that product's allocated units
                                                                    setAllocatedUnits((prev) => {
                                                                        if (!prev[removedProductId]) return prev; // nothing to delete

                                                                        const { [removedProductId]: _, ...rest } = prev; // safely omit one key
                                                                        return rest;
                                                                    });
                                                                }}

                                                            >
                                                                Remove
                                                            </button>
                                                        </td>

                                                    </tr>
                                                ))
                                            }

                                        </tbody>
                                    </table>
                                </div>
                            </div>

                        </div>

                        <div className="row mb-3">
                            <div className="col-md-7">
                                <label htmlFor="">Instructions </label>
                                <textarea
                                    name="instructions"
                                    id="instructions"
                                    className="form-control"
                                    placeholder="Add instructions here"
                                    value={formik.values.instructions}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    rows={4}
                                    readOnly={!isFieldEditable(installationOrder?.status ?? 0, "Instructions")}
                                ></textarea>

                                {/* <div className="col-md-12 mt-5">
                                    <label htmlFor="">Proof of Installation</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        id="image"
                                        name="image"
                                        multiple
                                        onChange={(e) => {
                                            const files = Array.from(e.target.files || []);
                                            const newPreviews = files.map((file) => ({
                                                file,
                                                url: URL.createObjectURL(file),
                                            }));
                                            setPreviewFiles((prev) => [...prev, ...newPreviews]);
                                        }}
                                    />

                                    <button
                                        type="button"
                                        className="btn btn-success mt-3"
                                        onClick={async () => {
                                            try {
                                                const uploads = await Promise.all(
                                                    previewFiles.map(async ({ file }) => {
                                                        const path = await uploadFile(file); // ✅ Reuse function
                                                        return {
                                                            file_name: file.name,
                                                            file_url: path,
                                                            content_type: file.type,
                                                            uploaded_date: new Date().toISOString(),
                                                        };
                                                    })
                                                );

                                                const allFiles = [...uploadedFileMeta, ...uploads];
                                                const uniqueByName = allFiles.filter(
                                                    (file, index, self) =>
                                                        index === self.findIndex((f) => f.file_name === file.file_name)
                                                );

                                                setUploadedFileMeta(uniqueByName);
                                                setPreviewFiles([]); // clear previews after upload
                                                toast.success("Files uploaded successfully!");
                                            } catch (err) {
                                                console.error("Upload error:", err);
                                                toast.error("Failed to upload files.");
                                            }
                                        }}
                                    >
                                        Upload
                                    </button>

                                    <div className="row mt-3">
                                        {uploadedFileMeta.map((file, index) => (
                                            <div key={index} className="col-md-3 position-relative mb-3">
                                                <div
                                                    className="position-absolute top-0 end-0 bg-danger text-white rounded-circle"
                                                    style={{
                                                        width: "20px",
                                                        height: "20px",
                                                        textAlign: "center",
                                                        cursor: "pointer",
                                                        zIndex: 2,
                                                    }}
                                                    onClick={() => {
                                                        setUploadedFileMeta((prev) => prev.filter((_, i) => i !== index));
                                                    }}
                                                >
                                                    &times;
                                                </div>
                                                <div className="border p-2">
                                                    <img
                                                        src={`${process.env.REACT_APP_API_URL}/${file.file_url.replace(/\\/g, "/")}`}
                                                        alt={file.file_name}
                                                        className="img-fluid"
                                                        style={{ height: "150px", objectFit: "cover", width: "100%" }}
                                                    />
                                                    <div className="small text-center mt-1 text-muted">{file.file_name}</div>
                                                </div>
                                            </div>
                                        ))}

                                        {previewFiles.map((item, index) => (
                                            <div key={`preview-${index}`} className="col-md-3 position-relative mb-3">
                                                <div
                                                    className="position-absolute top-0 end-0 bg-danger text-white rounded-circle"
                                                    style={{
                                                        width: "20px",
                                                        height: "20px",
                                                        textAlign: "center",
                                                        cursor: "pointer",
                                                        zIndex: 2,
                                                    }}
                                                    onClick={() => {
                                                        URL.revokeObjectURL(item.url);
                                                        setPreviewFiles(previewFiles.filter((_, i) => i !== index));
                                                    }}
                                                >
                                                    &times;
                                                </div>
                                                <div className="border p-2">
                                                    <img
                                                        src={item.url}
                                                        alt={`preview-${index}`}
                                                        className="img-fluid"
                                                        style={{ height: "150px", objectFit: "cover", width: "100%" }}
                                                    />
                                                    <div className="small text-center mt-1 text-muted">{item.file.name}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                </div> */}
                            </div>
                            <div className="col-md-5">
                                <table className="calculation-table">
                                    <tr>
                                        <th>Total Units</th>
                                        <td>{totalUnits}</td>
                                    </tr>
                                    <tr>
                                        <th>Discount</th>
                                        <td>
                                            $ <input type="number" name="discount" id="discount" value={Number(formik.values.discount).toFixed(2)} onChange={formik.handleChange}
                                                onBlur={formik.handleBlur} />
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Installation Charge</th>
                                        <td>$ <input type="number" name="installation_charge" id="installation_charge" value={Number(formik.values.installation_charge)?.toFixed(2) ?? 0.00} onChange={formik.handleChange} onBlur={formik.handleBlur} /></td>
                                    </tr>
                                    <tr>
                                        <th>Tax</th>
                                        <td>$ <input type="number" name="tax" id="tax" value={Number(formik.values.tax).toFixed(2)} onChange={formik.handleChange} onBlur={formik.handleBlur} /></td>
                                    </tr>
                                    <tr>
                                        <th>Subtotal</th>
                                        <td>$ {Number(formik.values.subtotal).toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <th>Total</th>
                                        <td>$ {Number(formik.values.total).toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <th>Difference</th>
                                        <td>$0.00</td>
                                    </tr>
                                </table>

                            </div>
                        </div>

                        {/* <div className="row mb-3">
              <div className="col-md-6">

              </div>
              <div className="col-md-6">
                <div style={mapContainerStyle}>
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    zoom={10}
                    center={selectedLocation || center}
                    onClick={handleMapClick}
                  >
                    {selectedLocation && <Marker position={selectedLocation} />}
                  </GoogleMap>
                </div>
              </div>

            </div> */}



                        <div className="row justify-content-end">
                            <div className="col-md-3 mt-3">
                                <label className="me-1 mb-1">Local Laws Compliance :</label><br />
                                <input
                                    type="checkbox"
                                    id="localLaws"
                                    name="localLawsChecked"
                                    checked={formik.values.localLawsChecked}
                                    onChange={formik.handleChange}
                                />
                                <span>{" "}I acknowledge adherence to local laws</span>

                            </div>
                        </div>

                        <button
                            type="submit"
                            className="btn btn-info mt-4 float-end"
                            disabled={formSubmitting || !formik.values.localLawsChecked}
                        >
                            Save Changes
                        </button>
                    </form>
                    {(showAllocateModal || showViewInventoryModal) && (
                        <div className="modal-backdrop show"></div>
                    )}
                    {/* <div className={`modal fade ${showAllocateModal ? "show d-block" : ""}`} tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Allocate Rotational Product </h5>
                  <button type="button" className="btn-close" onClick={() => setShowAllocateModal(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-4">
                      <label htmlFor="">Select Product:</label>
                    </div>
                    <div className="col-md-8">
                      <Select
                        name="productId"
                        id="productId"
                        options={rotationalproductOptions}
                        value={selectedProduct}
                        onChange={handleProductSelect}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-4">
                      <label htmlFor="">Select Warehouse:</label>
                    </div>
                    <div className="col-md-8">
                      <Select
                        name="warehouseId"
                        id="warehouseId"
                        options={warehouseOptions}
                        value={selectedWarehouse}
                        onChange={(selected) => {
                          setSelectedWarehouse(selected);
                          if (selectedProduct?.fullProduct && selected?.value) {
                            const rotationalId = selectedProduct.value;
                            const warehouseId = selected.value;
                            fetchSerializedUnits(rotationalId, warehouseId);
                          }
                        }}
                      />


                      <select className="form-control">
                        <option value="warehouse A">Warehouse A</option>
                        <option value="warehouse B">Warehouse B</option>
                      </select>
                    </div>
                  </div>
                  <p><strong>Quantity Required:</strong> 3</p>

                  * ✅ Check if this linked product already has one allocation
                  {(() => {
                    const hasAllocationForLinkedProduct = !!(linkedProductIdContext && allocatedUnits[linkedProductIdContext]);

                    return (
                      <table className="table table-bordered default-table">
                        <thead>
                          <tr>
                            <th>Serial No.</th>
                            <th>Warehouse</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {availableSerializedUnits?.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="text-center">No units available</td>
                            </tr>
                          ) : (
                            availableSerializedUnits?.map((unit, idx) => (
                              <tr key={unit.serial_id || idx}>
                                <td>{unit.serial_number || "N/A"}</td>
                                <td>{unit.warehouse_name}</td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-success"
                                    disabled={hasAllocationForLinkedProduct}
                                    onClick={() => {
                                      if (!linkedProductIdContext || !selectedProduct || !selectedWarehouse) return;

                                      const linkedProductId = linkedProductIdContext;
                                      const rotationalProductId = selectedProduct.value;
                                      const warehouseId = selectedWarehouse.value;

                                      const newUnit = {
                                        rotationalProductId,
                                        warehouseId,
                                        serial_id: unit.serial_id,
                                        serial_number: unit.serial_number,
                                      };

                                      setAllocatedUnits((prev) => {
                                        const existing = prev[linkedProductId] || [];
                                        const alreadyExists = existing.some(u => u.serial_id === unit.serial_id);
                                        if (alreadyExists) {
                                          toast.warning("This unit is already allocated.");
                                          return prev;
                                        }
                                        return {
                                          ...prev,
                                          [linkedProductId]: [...existing, newUnit],
                                        };
                                      });

                                      toast.success("Unit allocated.");
                                    }}
                                  >
                                    Allocate
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    );
                  })()}

                </div>
              </div>
            </div>
          </div> */}

                </div>
            </div >
        </div >
    );
};

export default SignageTeammemberEditInstallation;
